<script setup lang="ts">
import { ref, watch } from 'vue'
import { Button } from '@/components/ui/button'
import { Activity, Plus, Trash2, RefreshCw, CircleDot, ArrowRight } from 'lucide-vue-next'
import BaseVisualEditor from './BaseVisualEditor.vue'
import MermaidCodeEditor from './MermaidCodeEditor.vue'
import MermaidLivePreview from './MermaidLivePreview.vue'

const props = defineProps<{
  open: boolean
}>()

const emit = defineEmits<{
  'update:open': [value: boolean]
  'save': [code: string]
}>()

const showCode = ref(true)
const copied = ref(false)
const editableCode = ref('')

// Interfaces
interface SDState {
  id: string
  name: string
}

interface SDTransition {
  from: string
  to: string
  text: string
}

// Estados visuales
const states = ref<SDState[]>([
  { id: 'EnEspera', name: 'Esperando Pago' },
  { id: 'Procesando', name: 'Procesando Orden' },
  { id: 'Completado', name: 'Orden Completada' }
])

const transitions = ref<SDTransition[]>([
  { from: '[*]', to: 'EnEspera', text: 'Iniciar flujo' },
  { from: 'EnEspera', to: 'Procesando', text: 'Pago recibido' },
  { from: 'Procesando', to: 'Completado', text: 'Éxito' },
  { from: 'Completado', to: '[*]', text: '' }
])

// Acciones Estados
const addState = () => {
  const newId = `Estado_${states.value.length + 1}`
  states.value.push({ id: newId, name: `Nuevo Estado ${states.value.length + 1}` })
}
const removeState = (idx: number) => {
  states.value.splice(idx, 1)
}

// Acciones Transiciones
const addTransition = () => {
  const from = states.value.length > 0 ? states.value[0].id : '[*]'
  const to = states.value.length > 1 ? states.value[1].id : '[*]'
  transitions.value.push({ from, to, text: 'Nueva Transición' })
}
const removeTransition = (idx: number) => {
  transitions.value.splice(idx, 1)
}

// Generar Mermaid
const generateMermaid = () => {
  let code = `stateDiagram-v2\n`
  
  // Declarar estados (excepto [*] que es implícito)
  states.value.forEach(s => {
    const safeId = s.id.trim().replace(/[^a-zA-Z0-9_]/g, '')
    if (safeId) {
      if (s.name.trim()) {
        code += `    state "${s.name.trim().replace(/"/g, '')}" as ${safeId}\n`
      } else {
        code += `    state ${safeId}\n`
      }
    }
  })

  if (states.value.length > 0) code += `\n`

  // Declarar transiciones
  transitions.value.forEach(t => {
    let from = t.from.trim()
    let to = t.to.trim()
    
    if (from !== '[*]') from = from.replace(/[^a-zA-Z0-9_]/g, '')
    if (to !== '[*]') to = to.replace(/[^a-zA-Z0-9_]/g, '')

    if (from && to) {
      if (t.text && t.text.trim()) {
        code += `    ${from} --> ${to} : ${t.text.trim()}\n`
      } else {
        code += `    ${from} --> ${to}\n`
      }
    }
  })
  
  editableCode.value = code
}

// Sincronizar desde Código a Visual
const syncToVisual = () => {
  const code = editableCode.value
  const lines = code.split('\n')
  
  const newStates: SDState[] = []
  const newTrans: SDTransition[] = []
  
  const stateRegex1 = /^state\s+"([^"]+)"\s+as\s+([a-zA-Z0-9_]+)/
  const stateRegex2 = /^state\s+([a-zA-Z0-9_]+)/
  const transRegex1 = /^(\[\*\]|[a-zA-Z0-9_]+)\s*-->\s*(\[\*\]|[a-zA-Z0-9_]+)\s*:\s*(.+)$/
  const transRegex2 = /^(\[\*\]|[a-zA-Z0-9_]+)\s*-->\s*(\[\*\]|[a-zA-Z0-9_]+)/

  const inferredStates = new Set<string>()

  for (const line of lines) {
    const t = line.trim()
    if (!t || t.startsWith('stateDiagram')) continue
    
    if (stateRegex1.test(t)) {
      const match = t.match(stateRegex1)!
      newStates.push({ name: match[1], id: match[2] })
      inferredStates.add(match[2])
    } else if (stateRegex2.test(t)) {
      const match = t.match(stateRegex2)!
      newStates.push({ name: '', id: match[1] })
      inferredStates.add(match[1])
    } else if (transRegex1.test(t)) {
      const match = t.match(transRegex1)!
      newTrans.push({ from: match[1], to: match[2], text: match[3] })
      
      if (match[1] !== '[*]' && !inferredStates.has(match[1])) {
        inferredStates.add(match[1])
        newStates.push({ id: match[1], name: '' })
      }
      if (match[2] !== '[*]' && !inferredStates.has(match[2])) {
        inferredStates.add(match[2])
        newStates.push({ id: match[2], name: '' })
      }
    } else if (transRegex2.test(t)) {
      const match = t.match(transRegex2)!
      newTrans.push({ from: match[1], to: match[2], text: '' })
      
      if (match[1] !== '[*]' && !inferredStates.has(match[1])) {
        inferredStates.add(match[1])
        newStates.push({ id: match[1], name: '' })
      }
      if (match[2] !== '[*]' && !inferredStates.has(match[2])) {
        inferredStates.add(match[2])
        newStates.push({ id: match[2], name: '' })
      }
    }
  }

  // Actualizar solo si hay cambios reales para no borrar el estado inicial si el código está mal
  if (newStates.length > 0 || newTrans.length > 0) {
    states.value = newStates
    transitions.value = newTrans
  }
}

// Watchers
watch(() => props.open, (newVal) => {
  if (newVal) {
    const editingCode = localStorage.getItem('editingDiagramCode');
    if (editingCode) {
      setTimeout(() => {
        editableCode.value = editingCode;
        localStorage.removeItem('editingDiagramCode');
        syncToVisual();
      }, 300);
      return;
    }
  }

  if (newVal && !editableCode.value) {
    generateMermaid()
  }
}, { immediate: true })

watch([states, transitions], () => {
  generateMermaid()
}, { deep: true })

const copyToClipboard = async () => {
  try {
    await navigator.clipboard.writeText(editableCode.value)
    copied.value = true
    setTimeout(() => copied.value = false, 2000)
  } catch (err) {
    console.error('Failed to copy text: ', err)
  }
}

const saveAndClose = () => {
  emit('save', editableCode.value)
  emit('update:open', false)
}
</script>

<template>
  <BaseVisualEditor
    :open="open"
    @update:open="$emit('update:open', $event)"
    title="Diagrama de Estados (State Diagram)"
    description="Diseña máquinas de estado finitas para sistemas reactivos o flujos de vida de un objeto."
    :showCode="showCode"
    @update:showCode="showCode = $event"
    :copied="copied"
    @copy="copyToClipboard"
    @save="saveAndClose"
  >
    <template #icon>
      <Activity class="w-5 h-5 text-primary" />
    </template>

    <template #toolbar>
      <Button size="sm" variant="default" @click="addState"><CircleDot class="w-4 h-4 mr-2" /> Añadir Estado</Button>
      <Button size="sm" variant="secondary" @click="addTransition"><ArrowRight class="w-4 h-4 mr-2" /> Añadir Transición</Button>
    </template>

    <template #canvas>
      <div class="h-full w-full overflow-y-auto p-6 bg-slate-50 dark:bg-slate-900/50 flex flex-col gap-6">
        
        <!-- Estados -->
        <div class="bg-card border rounded-lg p-4 shadow-sm flex flex-col gap-4">
          <h3 class="font-semibold text-lg border-b pb-2 flex items-center gap-2">
            <CircleDot class="w-5 h-5 text-indigo-500"/> Definición de Estados
          </h3>
          
          <div v-if="states.length === 0" class="text-center text-muted-foreground py-4 text-xs border border-dashed rounded">
            Sin estados definidos (Nota: El estado inicial/final [*] siempre existe).
          </div>

          <div class="grid grid-cols-1 md:grid-cols-2 gap-3">
            <div 
              v-for="(st, idx) in states" 
              :key="'st'+idx"
              class="flex flex-col gap-2 bg-muted/40 border p-3 rounded-md relative group"
            >
              <button 
                @click="removeState(idx)" 
                class="absolute right-2 top-2 text-muted-foreground hover:bg-destructive hover:text-white transition-colors p-1 rounded opacity-0 group-hover:opacity-100"
                title="Eliminar Estado"
              >
                <Trash2 class="w-4 h-4" />
              </button>

              <div class="flex flex-col gap-1 pr-6">
                <label class="text-[10px] font-bold uppercase text-muted-foreground ml-1">ID Único (Sin espacios)</label>
                <input v-model="st.id" class="bg-background border rounded px-2 py-1 text-sm focus:ring-1 focus:outline-none font-mono" placeholder="Ej. Procesando" />
              </div>
              <div class="flex flex-col gap-1">
                <label class="text-[10px] font-bold uppercase text-muted-foreground ml-1">Nombre Visual / Descripción (Opcional)</label>
                <input v-model="st.name" class="bg-background border rounded px-2 py-1 text-sm focus:ring-1 focus:outline-none" placeholder="Ej. Verificando Pago..." />
              </div>
            </div>
          </div>
        </div>

        <!-- Transiciones -->
        <div class="bg-card border rounded-lg p-4 shadow-sm flex flex-col gap-4">
          <h3 class="font-semibold text-lg border-b pb-2 flex items-center gap-2">
            <ArrowRight class="w-5 h-5 text-emerald-500"/> Flujo de Transiciones
          </h3>
          
          <div v-if="transitions.length === 0" class="text-center text-muted-foreground py-4 text-xs border border-dashed rounded">
            No hay transiciones. El diagrama estará vacío.
          </div>

          <div class="flex flex-col gap-2">
            <div 
              v-for="(t, idx) in transitions" 
              :key="'tr'+idx"
              class="flex flex-wrap items-center gap-2 bg-background border p-2 rounded-md shadow-sm"
            >
              <!-- Desde -->
              <div class="flex flex-col gap-1 min-w-[120px] flex-1">
                <label class="text-[10px] font-bold uppercase text-muted-foreground ml-1">Origen (Desde)</label>
                <select v-model="t.from" class="bg-muted border rounded px-2 py-1 text-sm focus:outline-none">
                  <option value="[*]">[*] Inicial / Final</option>
                  <option v-for="s in states" :key="'from_'+s.id" :value="s.id">{{ s.id }}</option>
                </select>
              </div>

              <!-- Flecha (Visual) -->
              <div class="flex items-center justify-center shrink-0 pt-4">
                <ArrowRight class="w-4 h-4 text-muted-foreground" />
              </div>

              <!-- Hacia -->
              <div class="flex flex-col gap-1 min-w-[120px] flex-1">
                <label class="text-[10px] font-bold uppercase text-muted-foreground ml-1">Destino (Hacia)</label>
                <select v-model="t.to" class="bg-muted border rounded px-2 py-1 text-sm focus:outline-none">
                  <option value="[*]">[*] Inicial / Final</option>
                  <option v-for="s in states" :key="'to_'+s.id" :value="s.id">{{ s.id }}</option>
                </select>
              </div>

              <!-- Condición / Texto -->
              <div class="flex flex-col gap-1 min-w-[150px] flex-2">
                <label class="text-[10px] font-bold uppercase text-muted-foreground ml-1">Condición / Evento (Opcional)</label>
                <input v-model="t.text" class="bg-background border rounded px-2 py-1 text-sm focus:outline-none focus:ring-1" placeholder="Ej. click_button" />
              </div>

              <!-- Eliminar -->
              <div class="flex items-center justify-center shrink-0 pt-4">
                <button 
                  @click="removeTransition(idx)" 
                  class="text-muted-foreground hover:bg-destructive hover:text-white p-1 rounded transition-colors"
                >
                  <Trash2 class="w-4 h-4" />
                </button>
              </div>
            </div>
          </div>
        </div>

      </div>
    </template>

    <template #code-panel>
      <div class="text-sm font-bold p-2 bg-muted-foreground/10 text-muted-foreground flex items-center justify-between border-b shrink-0">
        Mermaid (Sincronizado)
        <Button size="sm" variant="default" class="h-7 text-xs" @click="syncToVisual">
          <RefreshCw class="w-3 h-3 mr-2" />
          Sincronizar a Visual
        </Button>
      </div>
      <MermaidCodeEditor v-model="editableCode" class="flex-1 min-h-0" />
    </template>

    <template #live-preview>
      <MermaidLivePreview :code="editableCode" />
    </template>
  </BaseVisualEditor>
</template>
